import type { 
  MeiliTaskStatus, 
  MultiMeiliTaskResponse,
  MultiMeiliTaskStatus
} from "types/extended-profile.types";

export type ProfileParam = {
  profile?: {
    name?: string;
    uei?: string;
    parent_uei?: string;
    associated_ueis?: string[];
    pronet_id?: string;
    last_updated?: string;
    contact_person?: string;
    capabilities_narrative?: string;
    capabilities_narrative_link?: string;
    email?: string;
    phone?: string;
    fax?: string;
    address?: string;
    trade_name?: string;
    cage_code?: string[];
    website?: string;
    country_code?: string;
    accepts_government_credit_card: string;
    congressional_district?: string;
    gsa_contract?: string;
    metropolitan_area?: string;
    user_id?: string; // This is the same as the pronet_id
  };
  organization_ownership?: {
    legal_structure?: string;
    current_principals?: string[];
    bd_servicing_office?: string;
  };
  certifications?: {
    sba_certifications?: {
      // _comment?: string;
      "8a"?: {
        status?: string;
        date?: string;
        graduation_date?: string;
        graduation_status?: string;
      };
    };
    ownership_certifications?: string[];
    non_federal_certifications?: string[];
  };
  naics?: {
    code?: string;
    description?: string[];
    buy_green?: boolean;
    small?: boolean;
  }[];
  service_information?: {
    special_equipment?: string;
    business_percentages?: string[];
    bonding_levels?: {
      construction_per_contract?: string;
      construction_aggregate?: string;
      service_per_contract?: string;
      service_aggregate?: string;
    };
    keywords?: string;
    miscellaneous?: string;
    quality_assurance?: string[];
    electronic_data?: string;
  };
  export_profile?: {
    status?: string;
    business_activities?: string[];
    exporting_to?: string[];
    desired_export?: string;
    description_export?: string[];
  };
  performance_history?: {
    name?: string;
    contract?: string;
    value?: string;
    start_date?: string;
    end_date?: string;
    contact?: string;
    phone?: string;
  }[];
};

export type ResultsParam = {
  UEI: string;
  NAICS: string;
  PSC: string;
  state: string;
  keyword: string;
  size: string;
  bondingLevel: string;
  qualityAssuranceStandards: string;
  natureOfBusiness: string;
  profileStatus: string;
  location: string;
  sbacertifications: string;
  ownershipAndSelfCertifications: string;
  capabilityKeywords: string;
  privilegedSearch: string;
}[];

/**
 *
 * @returns Business profile data from DSBS
 */
export async function getProfile<ProfileParam>(pronetId): Promise<any> {
  // const proId = params.queryKey[0];
  try {
    const response = await fetch(`/_api/firms/${pronetId}`);
    if (!response.ok) {
      throw new Error("Failed to fetch profile data");
    }
    const data = await response.json();
    return data.results;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function getProfileV2(uei, cage_code): Promise<any> {
  try {
    const response = await fetch(
      `/_api/v2/profile/${uei}/${cage_code}`
    );

    if (!response.ok) {
      const error = new Error(
        `Failed to fetch extended profile data: ${response.status}`
      );
      (error as any).status = response.status; // Attach status code to error
      throw error;
    }

    const data = await response.json();

    if (data?.entity) {
      return data;
    }

    throw new Error("Profile data not found");
  } catch (error) {
    console.error("Error fetching meili profile: ", error);
    throw error
  }
}

export async function getProfileWithUEIV2(uei): Promise<any> {
  try {
    const response = await fetch(
      `/_api/v2/profile/${uei}`
    );

    if (!response.ok) {
      const error = new Error(
        `Failed to fetch extended profile data: ${response.status}`
      );
      (error as any).status = response.status; // Attach status code to error
      throw error;
    }

    const data = await response.json();

    if (data?.entity) {
      return data;
    }

    throw new Error("Profile data not found");
  } catch (error) {
    console.error("Error fetching meili profile: ", error);
    throw error
  }
}

export async function getExtendedProfileData(uei: string): Promise<any> {
  try {
    const response = await fetch(`/_api/v2/extended-profile/${uei}`, {method: "POST"});
    
    if (!response.ok) {
        const error = new Error(
          `Failed to fetch extended profile data: ${response.status}`
        );
        (error as any).status = response.status; // Attach status code to error
        throw error;
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error("Error fetching extended profile data: ", error);
    throw error
  }
}

export async function getSamEntity(uei, cage_code): Promise<any> {

  const fetchUrl = cage_code ?
    `/_api/v2/sam-entity/entity/${uei}/${cage_code}` :
    `/_api/v2/sam-entity/entity/${uei}`;

  try {
    const response = await fetch(fetchUrl);

    if (!response.ok) {
      const error = new Error(
        `Failed to fetch SAM entity data: ${response.status}`
      );
      (error as any).status = response.status; // Attach status code to error
      throw error;
    }

    const data = await response.json();

    if (data) {
      return data;
    }

    throw new Error("SAM entity data not found");
  } catch (error) {
    console.error("Error fetching SAM entity data: ", error);
    throw error
  }
}

export async function getSamEntityResults(uei): Promise<any> {
  try {
    const response = await fetch(
      `/_api/v2/sam-entity/search/${uei}`
    );

    if (!response.ok) {
      const error = new Error(
        `Failed to fetch SAM entity data: ${response.status}`
      );
      (error as any).status = response.status; // Attach status code to error
      throw error;
    }

    const data = await response.json();

    if (data) {
      return data;
    }

    throw new Error("SAM entity data not found");
  } catch (error) {
    console.error("Error fetching SAM entity data: ", error);
    throw error
  }
}

export async function updateExtendedProfileData(uei: string, payload: string): Promise<any> {
  try {
    const response = await fetch(
      `/_api/v2/extended-profile/${uei}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: payload,
      }
    );

    if (!response.ok) {
      throw new Error("Failed to update extended profile data");
    }

    return response;
  } catch (error) {
    console.error("Error fetching extended profile data: ", error);
    throw error
  }
}

export async function getResults<ResultsParam>(params): Promise<any> {
  try {
    const response = await fetch("/_api/firms", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch results data");
    }

    const data = await response.json();
    return data as any[];
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function getBusinessResults(params): Promise<any> {
  try {
    const response = await fetch("/_api/getbusiness", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch results data");
    }

    const data = await response.json();
    console.log("=======158***", data);
    return data as any;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function getQuickResults(): Promise<any> {
  try {
    const response = await fetch("/_api/quick-results");
    if (!response.ok) {
      throw new Error("Failed to fetch quick results data");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function getTotalActiveBusinesses(): Promise<any> {
  try {
    const response = await fetch("/_api/total_active");
    if (!response.ok) {
      throw new Error("Failed to fetch quick results data");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function sendFeedback(feedbackData: any): Promise<any> {
  try {
    const response = await fetch("/_api/feedback/create", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(feedbackData),
    });

    if (!response.ok) {
      throw new Error("Failed to send feedback.");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}
export async function getbusinessByUEI(params): Promise<any> {
  try {
    const response = await fetch("/_api/getbusinessByUEI", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch results data");
    }

    const data = await response.json();
    return data as any;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function updatePerformanceHistory(uei: string, payload: any): Promise<any> {
  try {
    const response = await fetch(`/_api/v2/performance-history/${uei}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error("Failed to update performance history");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating performance history: ", error);
    throw error;
  }
}

export async function getPerformanceHistory(uei: string): Promise<any> {
  try {
    const response = await fetch(`/_api/v2/performance-history/${uei}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch performance history");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching performance history: ", error);
    throw error;
  }
}

export async function uploadCapabilityStatmentFile(
  uei: string,
  file: File // Use actual File object instead of encoded object
): Promise<any> {
  try {
    const formData = new FormData();
    formData.append('capability_statement_file', file);
    // formData.append('uei', uei); // Add UEI to form data

    const response = await fetch(`/_api/v2/extended-profile/${uei}`, {
      method: "PUT",
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Failed to upload capability statement file");
    }

    return response;
  } catch (error) {
    console.error("Error uploading capability statement file: ", error);
    throw error;
  }
}

export async function getMeiliTaskStatus(taskUid: string, label: string): Promise<MeiliTaskStatus> {
  try {
    const response = await fetch(`/_api/v2/extended-profile/task/${taskUid}/status`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch Meili task status");
    }

    const data = await response.json();
    return {
      status: data.status,
      label: label,
      taskUid: taskUid,
    } as MeiliTaskStatus;
  } catch (error) {
    console.error("Error fetching Meili task status: ", error);
    throw error;
  }
}

export async function getMultiMeiliTaskStatus(group: MultiMeiliTaskStatus): Promise<MultiMeiliTaskResponse> {
  try {
    const response = await fetch(`/_api/v2/performance-history/tasks/status`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(group),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch Meili task statuses");
    }

    const data = await response.json();
    return {
      statuses: data.statuses,
      label: group.label,
      id: group.id,
    } as MultiMeiliTaskResponse;
  } catch (error) {
    console.error("Error fetching Meili task statuses: ", error);
    throw error;
  }
}

export async function createSizeDetermination(uei: string, payload: string): Promise<any> {
  try {
    const response = await fetch(
      `/_api/v2/size-determination/${uei}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: payload,
      }
    );

    if (!response.ok) {
      throw new Error("Failed to create size determination");
    }

    return response;
  } catch (error) {
    console.error(`Error creating size determination for uei ${uei}: `, error);
    throw error
  }
}

export async function getSizeDeterminationData(uei: string): Promise<any> {
  try {
    const response = await fetch(`/_api/v2/size-determination/${uei}`, {method: "GET"});
    
    if (!response.ok) {
        const error = new Error(
          `Failed to fetch size determination data: ${response.status}`
        );
        (error as any).status = response.status; // Attach status code to error
        throw error;
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error("Error fetching extended profile data: ", error);
    throw error
  }
}

export async function updateSizeDetermination(uei: string, id: string, payload: string): Promise<any> {
  try {
    const response = await fetch(
      `/_api/v2/size-determination/${uei}/${id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: payload,
      }
    );

    if (!response.ok) {
      throw new Error("Failed to update size determination");
    }

    return response;
  } catch (error) {
    console.error(`Error updating size determination for uei ${uei} and id ${id}: `, error);
    throw error
  }
}

export async function deleteSizeDetermination(uei: string, id: string): Promise<any> {
  try {
    const response = await fetch(`/_api/v2/size-determination/${uei}/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Failed to delete size determination");
    }

    return response;
  } catch (error) {
    console.error(`Error deleting size determination with id ${id}: `, error);
    throw error
  }
}